import {
	useQuery,
	useMutation,
	UseQueryResult,
	UseMutationResult,
} from '@tanstack/react-query'

import { PaginatedResponse } from '@core/api/dtos/common/types'
import { Campaign } from '@core/api/entities/campaign/campaign'
import { Voice } from '@core/api/entities/campaign/voice'
import { CampaignMapper } from '@core/api/mappers/campaign/campaignMapper'
import { VoiceMapper } from '@core/api/mappers/campaign/voiceMapper'
import { campaignService } from '@core/api/services/campaignService'
import { DEFAULT_PARAMS } from '@core/hooks/common'
import { useAccountConfig } from '@core/hooks/useAccountConfig'

/**
 * Paginate campaigns
 */
export const usePaginateCampaigns = ({
	config,
	options,
}: any = DEFAULT_PARAMS): UseQueryResult<
	PaginatedResponse<Campaign>,
	Error
> => {
	const accountConfig = useAccountConfig(config, true)

	const QUERY_KEY = [
		'campaigns',
		{
			...accountConfig?.params,
		},
	]

	return useQuery({
		queryKey: QUERY_KEY,
		queryFn: () =>
			campaignService
				.paginateCampaigns(accountConfig)
				.then(({ data: response }) => {
					return {
						data: CampaignMapper.toEntities(response.data),
						meta: response.meta,
					}
				}),
		...options,
	})
}

/**
 * Get a campaign by id
 */
export const useCampaign = ({
	id,
	config,
	options,
}: any = DEFAULT_PARAMS): UseQueryResult<Campaign, Error> => {
	const QUERY_KEY = [
		'campaign',
		id,
		{
			...config?.params,
		},
	]

	return useQuery({
		queryKey: QUERY_KEY,
		queryFn: () =>
			campaignService.getCampaign(id, config).then(({ data: response }) => {
				return CampaignMapper.toEntity(response.data)
			}),
		...options,
	})
}

/**
 * Update a campaign
 */
export const useUpdateCampaign = ({
	options,
	config,
}: any = {}): UseMutationResult<
	any,
	Error,
	{ id: number; data: any },
	unknown
> => {
	return useMutation({
		mutationFn: ({ id, data }: { id: number; data: any }) =>
			campaignService.updateCampaign(id, data, config),
		...options,
	})
}

/**
 * Delete a campaign
 */
export const useDeleteCampaign = ({
	config,
	options,
}: any = {}): UseMutationResult<any, Error, number, unknown> => {
	return useMutation({
		mutationFn: (id: number) => campaignService.deleteCampaign(id, config),
		...options,
	})
}

/**
 * Create a campaign
 */
export const useCreateCampaign = ({
	options,
	config,
}: any = {}): UseMutationResult<any, Error, any, unknown> => {
	return useMutation({
		mutationFn: (data: any) => campaignService.createCampaign(data, config),
		...options,
	})
}

/**
 * Get campaigns for a group
 */
export const useGroupCampaigns = ({
	groupId,
	config,
	options,
}: any = DEFAULT_PARAMS): UseQueryResult<
	PaginatedResponse<Campaign>,
	Error
> => {
	const QUERY_KEY = [
		'group-campaigns',
		groupId,
		{
			...config?.params,
		},
	]

	return useQuery({
		queryKey: QUERY_KEY,
		queryFn: () =>
			campaignService
				.getGroupCampaigns(groupId, config)
				.then(({ data: response }) => {
					return {
						data: CampaignMapper.toEntities(response.data),
						meta: response.meta,
					}
				}),
		enabled: !!groupId,
		...options,
	})
}

/**
 * Update campaigns for a group
 */
export const useUpdateGroupCampaigns = ({
	config,
	options,
}: any = {}): UseMutationResult<
	any,
	Error,
	{ groupId: number; announcements: number[] },
	unknown
> => {
	return useMutation({
		mutationFn: ({
			groupId,
			announcements,
		}: {
			groupId: number
			announcements: number[]
		}) =>
			campaignService.updateGroupCampaigns(groupId, { announcements }, config),
		...options,
	})
}

/**
 * Add campaigns to a group
 */
export const useAddCampaignToGroup = ({
	config,
	options,
}: any = {}): UseMutationResult<
	any,
	Error,
	{ groupId: number; data: { announcements: number[] } },
	unknown
> => {
	return useMutation({
		mutationFn: ({
			groupId,
			data,
		}: {
			groupId: number
			data: { announcements: number[] }
		}) => campaignService.addCampaignToGroup(groupId, data, config),
		...options,
	})
}

/**
 * Remove campaigns from a group
 */
export const useRemoveCampaignFromGroup = ({
	config,
	options,
}: any = {}): UseMutationResult<
	any,
	Error,
	{ groupId: number; data: { announcements: number[] } },
	unknown
> => {
	return useMutation({
		mutationFn: ({
			groupId,
			data,
		}: {
			groupId: number
			data: { announcements: number[] }
		}) => campaignService.removeCampaignFromGroup(groupId, data, config),
		...options,
	})
}

/**
 * Get available ElevenLabs voices
 */
export const useVoices = ({
	config,
	options,
}: any = DEFAULT_PARAMS): UseQueryResult<Voice[], Error> => {
	const QUERY_KEY = ['voices']

	return useQuery({
		queryKey: QUERY_KEY,
		queryFn: () =>
			campaignService.getVoices(config).then(({ data }) => {
				// Data is already the filtered voices array
				return VoiceMapper.toEntities(data)
			}),
		staleTime: 1000 * 60 * 60, // 1 hour - voices don't change often
		retry: 2,
		retryDelay: 10000, // 10 seconds between retries
		...options,
	})
}
